/**
 * Gizli testleri tarayicida, izole bir Web Worker icinde calistirir.
 *
 * Beklenen degerler sunucudan DUZ METIN gelmez — SHA-256 hash'i gelir.
 * Worker ogrencinin fonksiyonunu calistirir, sonucu ayni kanonik bicimde
 * hash'ler ve hash'leri karsilastirir. Boylece dogru cevap ag trafiginde
 * gorunmez.
 */
export interface TestCase {
  args: unknown[];
  expectedHash: string;
}

export interface TestRunResult {
  passed: number;
  total: number;
  failures: { index: number; message: string }[];
}

const TIMEOUT_MS = 4000;

// Worker kaynagi string olarak tutulur — ayri dosya + build ayari gerekmesin diye
// Blob URL uzerinden yuklenir.
// Test edilebilirlik icin disa acik: Node tarafinda sahte bir `self` ile
// calistirilip gercek soru verisiyle dogrulanabiliyor.
export const WORKER_SRC = `
function stableStringify(value) {
  if (value === null || typeof value !== 'object') {
    var s = JSON.stringify(value);
    return s === undefined ? 'undefined' : s;
  }
  if (Array.isArray(value)) return '[' + value.map(stableStringify).join(',') + ']';
  var keys = Object.keys(value).sort();
  return '{' + keys.map(function (k) {
    return JSON.stringify(k) + ':' + stableStringify(value[k]);
  }).join(',') + '}';
}

async function sha256(text) {
  var buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf))
    .map(function (b) { return b.toString(16).padStart(2, '0'); })
    .join('');
}

self.onmessage = async function (e) {
  var code = e.data.code;
  var entry = e.data.entry;
  var cases = e.data.cases;
  var failures = [];
  var passed = 0;

  var fn;
  try {
    // eslint-disable-next-line no-new-func
    fn = new Function(code + '\\nreturn typeof ' + entry + " === 'function' ? " + entry + ' : null;')();
  } catch (err) {
    self.postMessage({
      passed: 0,
      total: cases.length,
      failures: [{ index: -1, message: 'Kodun çalıştırılamadı: ' + err.message }],
    });
    return;
  }
  if (!fn) {
    self.postMessage({
      passed: 0,
      total: cases.length,
      failures: [{ index: -1, message: '"' + entry + '" adında bir fonksiyon bulunamadı.' }],
    });
    return;
  }

  for (var i = 0; i < cases.length; i++) {
    try {
      // Her case kendi kopyasiyla calissin — mutasyon yapan cozumler
      // sonraki case'leri bozmasin
      var args = JSON.parse(JSON.stringify(cases[i].args));
      var out = fn.apply(null, args);
      if (out && typeof out.then === 'function') {
        failures.push({ index: i, message: 'Fonksiyon Promise döndürdü; doğrudan değer döndürmeli.' });
        continue;
      }
      var got = await sha256(stableStringify(out));
      if (got === cases[i].expectedHash) passed++;
      else failures.push({ index: i, message: 'Beklenen sonuç çıkmadı.' });
    } catch (err) {
      failures.push({ index: i, message: 'Hata: ' + err.message });
    }
  }

  self.postMessage({ passed: passed, total: cases.length, failures: failures });
};
`;

export function runJsTests(
  code: string,
  entry: string,
  cases: TestCase[],
): Promise<TestRunResult> {
  return new Promise((resolve) => {
    const total = cases.length;
    let settled = false;
    const finish = (r: TestRunResult) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      worker.terminate();
      URL.revokeObjectURL(url);
      resolve(r);
    };

    const url = URL.createObjectURL(new Blob([WORKER_SRC], { type: "text/javascript" }));
    const worker = new Worker(url);

    // Sonsuz donguye giren cozum sekmeyi kilitlemesin
    const timer = setTimeout(
      () =>
        finish({
          passed: 0,
          total,
          failures: [
            { index: -1, message: "Kodun çok uzun sürdü — sonsuz döngü olabilir." },
          ],
        }),
      TIMEOUT_MS,
    );

    worker.onmessage = (e) => finish(e.data as TestRunResult);
    worker.onerror = (e) =>
      finish({
        passed: 0,
        total,
        failures: [{ index: -1, message: e.message || "Kod çalıştırılamadı." }],
      });

    worker.postMessage({ code, entry, cases });
  });
}
